import {
  InvoiceDetail,
  InvoiceDetailType,
  InvoiceApprovalInputs,
  InvoiceApprovalProps,
  DashboardBillingValues,
} from "libLegacy/api/models";
import { InvoiceDetailCardProps } from "components/admin/InvoiceDetailCard";
import ptBR from "date-fns/locale/pt-BR";

export const parseValidations = (
  rawInconsistencies: Array<InvoiceDetail>,
  parsedInputs: Record<keyof InvoiceApprovalInputs, string>,
  variant: InvoiceDetailCardProps["variant"],
  rawInputs: InvoiceApprovalInputs
): Array<InvoiceDetailCardProps> => {
  return rawInconsistencies.map(({ title, description, type }) => {
    const parsedItem: InvoiceDetailCardProps = {
      title,
      meaning: description,
      variant,
    };
    const {
      previousBalanceKwh,
      injectedEnergyKwh,
      compensatedEnergyKwh,
      expectedBalanceKwh,
      invoiceBalanceKwh,
      invoiceConsumptionKwh,
      minimumConsumptionKwh,
      expectedCompensatedEnergyKwh,
    } = parsedInputs;

    switch (type) {
      case InvoiceDetailType.CREDIT_BALANCE:
        parsedItem.calculation = [
          {
            key: "Saldo de créditos do mês anterior",
            value: previousBalanceKwh,
          },
          {
            key: "Energia injetada do mês atual",
            value: `+ ${injectedEnergyKwh}`,
          },
          {
            key: "Energia compensada no mês atual",
            value: `- ${compensatedEnergyKwh}`,
            variant: "negative",
          },
          {
            key: "Saldo de créditos",
            value: expectedBalanceKwh,
            variant: "positive",
          },
        ];
        parsedItem.cardData = [
          { title: "Valor na Fatura", value: invoiceBalanceKwh },
          { title: "Valor esperado", value: expectedBalanceKwh },
        ];
        break;
      case InvoiceDetailType.COMPENSATED_ENERGY:
        const compensatedRaw = +rawInputs.expectedCompensatedEnergyKwh;
        const creditsTotalKwhRaw =
          +rawInputs.injectedEnergyKwh + +rawInputs.previousBalanceKwh;

        parsedItem.calcSteps = [
          {
            title: "A) Quanto consumo pode ser compensado na sua fatura",
            items: [
              { key: "Consumo", value: invoiceConsumptionKwh },
              {
                key: "Consumo Mínimo",
                value: `- ${minimumConsumptionKwh}`,
                variant: "negative",
              },
              {
                key: "Valor possível de ser compensado",
                value: expectedCompensatedEnergyKwh,
                variant: "positive",
              },
            ],
          },
          {
            title: "B) Quanto de créditos podem ser utilizados",
            items: [
              {
                key: "Saldo de créditos anterior",
                value: expectedBalanceKwh,
              },
              {
                key: "Energia injetada do mês atual",
                value: `+ ${injectedEnergyKwh}`,
              },
              {
                key: "Valor possível de ser compensado",
                value: `${creditsTotalKwhRaw.toLocaleString(ptBR.code, {
                  maximumFractionDigits: 0,
                })} kwh`,
                variant: "positive",
              },
            ],
          },
        ];

        if (compensatedRaw < creditsTotalKwhRaw) {
          // shows how compensableConsumption was calculated
          parsedItem.calculation = [
            {
              key: "Consumo",
              value: `${invoiceConsumptionKwh}`,
            },
            {
              key: "Consumo mínimo",
              tooltipText: "Não pode ser compensado",
              value: `- ${minimumConsumptionKwh}`,
              variant: "negative",
            },
            {
              key: "Energia compensada",
              value: `${expectedCompensatedEnergyKwh}`,
              variant: "positive",
            },
          ];
        } else {
          // shows how credits was calculated
          parsedItem.calculation = [
            {
              key: "Saldo de créditos anterior",
              value: `${expectedBalanceKwh}`,
            },
            {
              key: "Energia injetada do mês atual",
              value: `+ ${injectedEnergyKwh}`,
            },
            {
              key: "Energia compensada",
              value: `${expectedCompensatedEnergyKwh}`,
              variant: "positive",
            },
          ];
        }

        parsedItem.cardData = [
          {
            title: "Valor na Fatura",
            value: compensatedEnergyKwh,
          },
          {
            title: "Valor esperado",
            value: expectedCompensatedEnergyKwh,
          },
        ];
        break;
    }
    return parsedItem;
  });
};

export const parseInputs = (
  inputs: InvoiceApprovalInputs
): Record<keyof InvoiceApprovalInputs, string> => {
  const parsedInputs: Partial<Record<keyof InvoiceApprovalInputs, string>> = {};

  Object.keys(inputs).forEach((key) => {
    let displayValue;

    if (key.endsWith("Brl")) {
      displayValue = inputs[key]
        ? (+inputs[key]).toLocaleString(ptBR.code, {
            style: "currency",
            currency: "BRL",
          })
        : "R$ -";
    } else if (key.endsWith("Date")) {
      displayValue = Date.parse(inputs[key])
        ? new Date(inputs[key]).toLocaleDateString(ptBR.code)
        : " - ";
    } else if (key.endsWith("Kwh")) {
      displayValue = inputs[key]
        ? `${(+inputs[key]).toLocaleString(ptBR.code, {
            maximumFractionDigits: 0,
          })} kwh`
        : "- kwh";
    } else {
      // if a key fits none of the options, just parse it as string and move on
      displayValue = inputs[key] ? inputs[key].toLocaleString() : " - ";
    }

    parsedInputs[key] = displayValue;
  });

  // casting makes explicit that the object is no longer Partial<>
  return parsedInputs as Record<keyof InvoiceApprovalInputs, string>;
};

export const formatInvoiceData = (
  invoiceData?: InvoiceApprovalProps,
  rawBalance?: DashboardBillingValues["finalBillingAmount"]
) => {
  const balance = rawBalance
    ? (+rawBalance).toLocaleString(ptBR.code, {
        style: "currency",
        currency: "BRL",
      })
    : "R$ -";

  if (invoiceData && invoiceData.status !== "UNKNOWN") {
    const parsedInputs = parseInputs(invoiceData.inputs);
    const dueDate = parsedInputs.dueDate;
    const warnings = invoiceData.alerts;

    const inconsistencies = parseValidations(
      invoiceData.inconsistencies,
      parsedInputs,
      "error",
      invoiceData.inputs
    );

    const consistencies = parseValidations(
      invoiceData.validated,
      parsedInputs,
      "success",
      invoiceData.inputs
    );

    return { balance, dueDate, inconsistencies, consistencies, warnings };
  } else {
    return {
      balance,
      dueDate: " - ",
      inconsistencies: [],
      consistencies: [],
      warnings: [],
    };
  }
};
